import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InterestSwipeSession from '../components/test/InterestSwipeSession';
import InterestClusterWidget from '../components/widgets/InterestClusterWidget';
import ContactBox from '../components/widgets/ContactBox';
import PageTutorialGuide from '../components/tutorial/PageTutorialGuide';
import TutorialInlineButton from '../components/tutorial/TutorialInlineButton';
import { resolveTutorialGuideSteps } from '../components/tutorial/tutorialGuideSteps';
import { usePageTutorial } from '../components/tutorial/usePageTutorial';
import { DEMO_INTEREST_QUESTIONNAIRE_PAPERS } from '../data/demoInterestTestPaper';
import { fetchTestPapers, fetchTestProgress } from '../services/testService';
import { pickDefaultInterestPaper, pickLocalized } from '../utils/testUtils';
import { useUserInterestVector } from '../hooks/useUserInterestVector';

function InterestTest() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const lang = i18n.language?.startsWith('sc') ? 'sc' : i18n.language?.startsWith('tc') ? 'tc' : 'en';
  const forceTutorial = searchParams.get('tutorial') === '1';
  const { guideOpen, openGuide, closeGuide } = usePageTutorial('interest', { forceOpen: forceTutorial });
  const statsTutorialRef = useRef(null);
  const sessionTutorialRef = useRef(null);
  const interestTutorialAnchors = {
    stats: statsTutorialRef,
    session: sessionTutorialRef
  };
  const interestGuideSteps = resolveTutorialGuideSteps('interest', t);
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usedDemoFallback, setUsedDemoFallback] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [savedProgress, setSavedProgress] = useState(null);
  const [vectorRefreshKey, setVectorRefreshKey] = useState(0);
  const [resultRevealState, setResultRevealState] = useState('visible');
  const resultTransitionRef = useRef(null);
  const resultRevealTimerRef = useRef([]);
  const { interestVector, interestBankVersion, hasInterestVector, reload: reloadInterestVector } = useUserInterestVector({
    enabled: !loading,
    refreshKey: vectorRefreshKey
  });

  const loadInterestProgress = useCallback(async () => {
    if (!paper?.slug || !localStorage.getItem('token')) {
      setSavedProgress(null);
      return;
    }
    try {
      const list = await fetchTestProgress('interest');
      const row = Array.isArray(list) ? list.find((r) => r.paperSlug === paper.slug) : null;
      setSavedProgress(row || null);
    } catch {
      setSavedProgress(null);
    }
  }, [paper?.slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError('');
      try {
        const data = await fetchTestPapers('interest');
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setPaper(pickDefaultInterestPaper(data));
          setUsedDemoFallback(false);
        } else {
          setPaper(DEMO_INTEREST_QUESTIONNAIRE_PAPERS[0]);
          setUsedDemoFallback(true);
        }
      } catch (error) {
        if (!cancelled) {
          setPaper(DEMO_INTEREST_QUESTIONNAIRE_PAPERS[0]);
          setUsedDemoFallback(true);
          setLoadError(t('interestTest.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleSessionReload = useCallback(async (reason) => {
    await loadInterestProgress();
    if (reason === 'submitted') {
      await reloadInterestVector();
      setVectorRefreshKey((key) => key + 1);
      setResultRevealState('visible');
    }
  }, [loadInterestProgress, reloadInterestVector]);

  const handleShowResults = useCallback(() => {
    resultRevealTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
    setResultRevealState('message');
    window.requestAnimationFrame(() => {
      resultTransitionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    resultRevealTimerRef.current = [
      window.setTimeout(() => {
        setResultRevealState('revealing');
        window.requestAnimationFrame(() => {
          statsTutorialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }, 800),
      window.setTimeout(() => setResultRevealState('visible'), 4400)
    ];
  }, []);

  useEffect(() => () => {
    resultRevealTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
  }, []);

  useEffect(() => {
    if (!loading && paper) {
      loadInterestProgress();
    }
  }, [loading, paper, loadInterestProgress]);

  if (loading) {
    return (
      <div className="mx-auto flex min-h-[50vh] w-full max-w-7xl items-center justify-center px-4 py-8">
        <p className="text-ink-muted">{t('interestTest.loading')}</p>
      </div>
    );
  }

  if (!paper) {
    return (
      <div className="mx-auto flex min-h-[50vh] w-full max-w-7xl items-center justify-center px-4 py-8">
        <p className="text-ink-muted">{t('interestTest.noActivities')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-3 py-5 sm:px-4 sm:py-8">
      <div className="mb-6 relative">
        <div className="absolute right-0 top-0 z-10">
          <TutorialInlineButton onClick={openGuide} />
        </div>
        <div className="text-center px-12 sm:px-16">
          <p className="text-sm font-semibold uppercase tracking-wide text-link">
            {t('interestTest.pageEyebrow')}
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-normal text-ink">
            {pickLocalized(paper.title, lang)}
          </h1>
          <p className="mx-auto mt-2 max-w-3xl text-sm leading-6 text-ink-muted">
            {pickLocalized(paper.paperInstruction, lang)}
          </p>
        </div>
      </div>

      {loadError ? (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100">
          {loadError}
        </div>
      ) : null}

      <div className="min-w-0 space-y-5 sm:space-y-6">
        <div ref={sessionTutorialRef} className="w-full min-w-0">
          <InterestSwipeSession
            paper={paper}
            usedDemoFallback={usedDemoFallback}
            savedProgress={savedProgress}
            onProgressReload={handleSessionReload}
            hasCompletedInterestTest={hasInterestVector}
            onShowResults={handleShowResults}
          />
        </div>

        {resultRevealState === 'message' ? (
          <section
            ref={resultTransitionRef}
            className="interest-result-transition overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-800 px-5 py-10 text-center text-white shadow-xl sm:px-8 sm:py-12"
            aria-live="polite"
          >
            <p className="text-2xl font-bold tracking-tight sm:text-3xl">
              {t('interestTest.resultTransitionTitle')}
            </p>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-white/80 sm:text-base">
              {t('interestTest.resultTransitionBody')}
            </p>
            <span className="mt-3 inline-block animate-bounce text-2xl" aria-hidden>↓</span>
          </section>
        ) : null}

        <section
          ref={statsTutorialRef}
          className={`w-full min-w-0 rounded-lg border border-edge bg-surface p-4 shadow-sm sm:p-5 ${
            resultRevealState === 'hidden' || resultRevealState === 'message' ? 'hidden' : ''
          }`}
        >
          <InterestClusterWidget
            key={vectorRefreshKey}
            interests={interestVector}
            interestBankVersion={interestBankVersion}
            variant="tiles"
            showTakeTestLink={false}
            hasCompletedTest={hasInterestVector}
            revealSequence={resultRevealState === 'revealing'}
            fullWidthRows
            embedded
          />
        </section>

        <ContactBox />
      </div>

      <PageTutorialGuide
        active={guideOpen}
        onClose={closeGuide}
        t={t}
        steps={interestGuideSteps}
        anchorRefs={interestTutorialAnchors}
      />
    </div>
  );
}

export default InterestTest;
